import { pick } from '../object';

/**
 * Matches items in @see matchingArray to items in @see sourceArray using
 * @see matchingKey & @see sourceKey respectively. When matches are found
 * @see propertiesToCopy are copied from the matched item to the source item
 * and a combined object is returned.
 *
 * @param sourceArray Source array of objects.
 * @param matchingArray Matching array of objects to copy properties off from.
 * @param sourceKey Used to match @see sourceArray items to @see matchingArray items.
 * @param matchingKey Used to match @see matchingArray items to @see sourceArray items.
 * @param propertiesToCopy Properties to copy from the matching object to the source object.
 * If empty, all properties are copied.
 *
 * @note Check unit tests for usage examples.
 */
export function mergeArrays<
  TSource extends Record<string, any>,
  TMatch extends Record<string, any>,
  TCopy extends keyof TMatch = keyof TMatch
>(
  sourceArray: TSource[],
  matchingArray: TMatch[],
  sourceKey: keyof TSource,
  matchingKey: keyof TMatch,
  propertiesToCopy: TCopy[] = []
): (TSource & Pick<TMatch, TCopy>)[] {
  const matchingMap = new Map(
    matchingArray.map((obj) => {
      return [obj[matchingKey], obj];
    })
  );

  return sourceArray.map((sourceObj) => {
    const matchingObj = matchingMap.get(sourceObj[sourceKey] as any);
    if (matchingObj) {
      const copiedProperties = (!propertiesToCopy.length) ? (
        matchingObj
      ) : pick(matchingObj, propertiesToCopy);
      return {
        ...sourceObj,
        ...copiedProperties
      };
    } else {
      return { ...sourceObj };
    }
  }) as (TSource & TMatch)[];
}
